import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { formatDocumentNumber } from '../utils/documentNumber';
import './WriterSeries.css';

const NAME_MAX_LENGTH = 120;
const DESCRIPTION_MAX_LENGTH = 1000;

const isAdminToken = (token) => typeof token === 'string' && token.trim().length > 0;

// Writer: passphrase in the body. Admin: Bearer token + the selected author.
const buildAuth = ({ passphrase, adminToken, adminAuthorHash }) => (
    isAdminToken(adminToken)
        ? {
            body: { authorHash: String(adminAuthorHash || '').trim() },
            config: { headers: { Authorization: `Bearer ${adminToken.trim()}` } },
        }
        : { body: { passphrase }, config: {} }
);

// 401 only comes back for an expired admin token (writers get 400/403).
const isUnauthorized = (requestError) => requestError?.response?.status === 401;

const describeError = (requestError, fallback) => {
    if (isUnauthorized(requestError)) {
        return 'Admin session expired — unlock again from the Master Dashboard.';
    }
    return requestError?.response?.data?.message || requestError?.message || fallback;
};

// postId -> { series, part } for every post in the given series list.
export const indexSeriesByPost = (seriesList) => {
    const map = new Map();
    (Array.isArray(seriesList) ? seriesList : []).forEach((entry) => {
        (Array.isArray(entry?.postIds) ? entry.postIds : []).forEach((postId, index) => {
            if (!map.has(String(postId))) {
                map.set(String(postId), { series: entry, part: index + 1 });
            }
        });
    });
    return map;
};

// "Series: X · Part N" on an Author Portal post card (nothing when the post
// is in no series).
export function AuthorPostSeriesLine({ membership }) {
    if (!membership) {
        return null;
    }
    return (
        <p className="author-post-series">
            {`Series: ${membership.series.name} · Part ${membership.part}`}
        </p>
    );
}

// Loads the signed-in writer's series (or the selected author's, in admin
// mode). Lives in App so the post cards can show "Series: X · Part N" even
// before the My Series tab is opened. `onUnauthorized` runs on a 401 so the
// portal can end the expired admin session, as its other requests do.
export const useAuthorSeries = ({ enabled, passphrase, adminToken, adminAuthorHash, onUnauthorized }) => {
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    // Latest callback, without reloading whenever the parent re-renders.
    const onUnauthorizedRef = useRef(onUnauthorized);
    useEffect(() => {
        onUnauthorizedRef.current = onUnauthorized;
    }, [onUnauthorized]);

    useEffect(() => {
        const admin = isAdminToken(adminToken);
        const canLoad = enabled && (admin ? Boolean(adminAuthorHash) : Boolean(passphrase));
        if (!canLoad) {
            setSeries([]);
            setError('');
            setLoading(false);
            return undefined;
        }

        let active = true;
        const auth = buildAuth({ passphrase, adminToken, adminAuthorHash });
        setLoading(true);
        setError('');

        axios.post(`${API_URL}/api/series/mine`, auth.body, auth.config)
            .then((response) => {
                if (active) {
                    setSeries(Array.isArray(response.data?.series) ? response.data.series : []);
                }
            })
            .catch((requestError) => {
                if (active) {
                    setSeries([]);
                    setError(describeError(requestError, 'Failed to load your series'));
                    if (isUnauthorized(requestError)) {
                        onUnauthorizedRef.current?.();
                    }
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [enabled, passphrase, adminToken, adminAuthorHash, reloadKey]);

    const reload = useCallback(() => setReloadKey((key) => key + 1), []);

    return { series, setSeries, loading, error, reload };
};

const draftFromSeries = (entry) => ({
    name: entry?.name || '',
    description: entry?.description || '',
    postIds: Array.isArray(entry?.postIds) ? entry.postIds.map(String) : [],
});

const isDraftChanged = (draft, entry) => {
    const saved = draftFromSeries(entry);
    return draft.name.trim() !== saved.name
        || draft.description.trim() !== saved.description
        || draft.postIds.join(',') !== saved.postIds.join(',');
};

const postLabel = (post) => {
    if (!post) {
        return '';
    }
    const number = formatDocumentNumber(post.documentNumber ?? post.copyNumber);
    return `#${number} — ${post.title}${post.date ? ` (${post.date})` : ''}`;
};

function SeriesCard({
    entry,
    postById,
    sortedPosts,
    seriesByPost,
    auth,
    onSaved,
    onDeleted,
    onActivity,
    onUnauthorized,
    defaultOpen = false,
}) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [draft, setDraft] = useState(() => draftFromSeries(entry));
    const [pickedPostId, setPickedPostId] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');

    // A fresh copy from the server (after a save or reload) resets the draft.
    useEffect(() => {
        setDraft(draftFromSeries(entry));
        setConfirmDelete(false);
    }, [entry]);

    const dirty = isDraftChanged(draft, entry);
    const savedPartCount = Array.isArray(entry.postIds) ? entry.postIds.length : 0;
    const draftIdSet = new Set(draft.postIds);

    const updateDraft = (changes) => {
        setDraft((previous) => ({ ...previous, ...changes }));
        setStatus('');
        setConfirmDelete(false);
        onActivity?.();
    };

    const movePart = (index, offset) => {
        const target = index + offset;
        if (target < 0 || target >= draft.postIds.length) {
            return;
        }
        const next = [...draft.postIds];
        [next[index], next[target]] = [next[target], next[index]];
        updateDraft({ postIds: next });
    };

    const removePart = (index) => {
        updateDraft({ postIds: draft.postIds.filter((_, position) => position !== index) });
    };

    const addPart = () => {
        if (!pickedPostId || draftIdSet.has(pickedPostId)) {
            return;
        }
        updateDraft({ postIds: [...draft.postIds, pickedPostId] });
        setPickedPostId('');
    };

    const handleSave = async (event) => {
        event.preventDefault();
        const name = draft.name.trim();
        if (!name) {
            setError('Series name is required');
            return;
        }

        setSaving(true);
        setError('');
        setStatus('');
        onActivity?.();
        try {
            const response = await axios.patch(
                `${API_URL}/api/series/${entry._id}`,
                {
                    ...auth.body,
                    name,
                    description: draft.description.trim(),
                    postIds: draft.postIds,
                },
                auth.config
            );
            onSaved(response.data?.series);
            setStatus('Series saved');
        } catch (requestError) {
            setError(describeError(requestError, 'Failed to save series'));
            if (isUnauthorized(requestError) && onUnauthorized) {
                onUnauthorized();
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            setStatus('Press delete again to confirm — your documents stay published');
            onActivity?.();
            return;
        }

        setDeleting(true);
        setError('');
        setStatus('');
        try {
            await axios.delete(`${API_URL}/api/series/${entry._id}`, { data: auth.body, ...auth.config });
            onDeleted(entry._id);
        } catch (requestError) {
            setError(describeError(requestError, 'Failed to delete series'));
            setDeleting(false);
            setConfirmDelete(false);
            if (isUnauthorized(requestError) && onUnauthorized) {
                onUnauthorized();
            }
        }
    };

    const handleDiscard = () => {
        setDraft(draftFromSeries(entry));
        setError('');
        setStatus('');
        setConfirmDelete(false);
    };

    const busy = saving || deleting;
    const liveHref = entry.slug ? `/series/${encodeURIComponent(entry.slug)}` : '';

    return (
        <article className="writer-series-card">
            <header className="writer-series-card-header">
                <div className="writer-series-card-heading">
                    <h4>{entry.name}</h4>
                    <p className="writer-series-card-meta">
                        {`${savedPartCount} part${savedPartCount === 1 ? '' : 's'}`}
                        {liveHref && savedPartCount > 0 && (
                            <>
                                {' · '}
                                <a href={liveHref} target="_blank" rel="noopener noreferrer">
                                    {`View on site: ${liveHref}`}
                                </a>
                            </>
                        )}
                        {savedPartCount === 0 && ' · Hidden on the site until it has a part'}
                    </p>
                </div>
                <button
                    type="button"
                    className="author-refresh-button"
                    onClick={() => setIsOpen((open) => !open)}
                    aria-expanded={isOpen}
                >
                    {isOpen ? 'Close' : 'Edit'}
                </button>
            </header>

            {isOpen && (
                <form className="writer-series-form" onSubmit={handleSave}>
                    {error && <div className="writer-controls-alert error">[ERROR] {error}</div>}
                    {status && <div className="writer-controls-alert status">{status}</div>}

                    <label className="writer-controls-field">
                        <span>Series Name *</span>
                        <input
                            type="text"
                            value={draft.name}
                            onChange={(event) => updateDraft({ name: event.target.value })}
                            maxLength={NAME_MAX_LENGTH}
                            required
                        />
                    </label>

                    <label className="writer-controls-field">
                        <span>Description</span>
                        <textarea
                            value={draft.description}
                            onChange={(event) => updateDraft({ description: event.target.value })}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            rows={3}
                            placeholder="One or two sentences shown on the series page"
                        />
                    </label>

                    <div className="writer-controls-field">
                        <span>Parts (reading order)</span>
                        {draft.postIds.length === 0 ? (
                            <p className="writer-series-empty">No parts yet — add your documents below.</p>
                        ) : (
                            <ol className="writer-series-parts">
                                {draft.postIds.map((postId, index) => {
                                    const post = postById.get(postId);
                                    return (
                                        <li key={postId} className="writer-series-part">
                                            <span className="writer-series-part-number">{`Part ${index + 1}`}</span>
                                            <span className="writer-series-part-title">
                                                {post ? postLabel(post) : `Document ${postId.slice(-6)} (not in your list)`}
                                            </span>
                                            <span className="writer-series-part-actions">
                                                <button
                                                    type="button"
                                                    onClick={() => movePart(index, -1)}
                                                    disabled={busy || index === 0}
                                                    aria-label={`Move part ${index + 1} up`}
                                                    title="Move up"
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => movePart(index, 1)}
                                                    disabled={busy || index === draft.postIds.length - 1}
                                                    aria-label={`Move part ${index + 1} down`}
                                                    title="Move down"
                                                >
                                                    ▼
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removePart(index)}
                                                    disabled={busy}
                                                    aria-label={`Remove part ${index + 1} from the series`}
                                                    title="Remove from series"
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                    </div>

                    <div className="writer-controls-field">
                        <span>Add a document</span>
                        <div className="writer-series-add">
                            <select
                                value={pickedPostId}
                                onChange={(event) => setPickedPostId(event.target.value)}
                                disabled={busy}
                            >
                                <option value="">Choose one of your documents…</option>
                                {sortedPosts.map((post) => {
                                    const id = String(post._id);
                                    if (draftIdSet.has(id)) {
                                        return null;
                                    }
                                    const owner = seriesByPost.get(id);
                                    const inOtherSeries = owner && owner.series._id !== entry._id;
                                    return (
                                        <option key={id} value={id} disabled={Boolean(inOtherSeries)}>
                                            {inOtherSeries
                                                ? `${postLabel(post)} — already in “${owner.series.name}”`
                                                : postLabel(post)}
                                        </option>
                                    );
                                })}
                            </select>
                            <button
                                type="button"
                                className="author-refresh-button"
                                onClick={addPart}
                                disabled={busy || !pickedPostId}
                            >
                                Add as Part {draft.postIds.length + 1}
                            </button>
                        </div>
                        <span className="field-hint">
                            A document can belong to one series. Changes go live when you save.
                        </span>
                    </div>

                    <div className="writer-controls-actions">
                        <button type="submit" className="writer-controls-submit" disabled={busy || !dirty}>
                            {saving ? 'Saving…' : 'Save Series'}
                        </button>
                        {dirty && (
                            <button
                                type="button"
                                className="writer-controls-delete"
                                onClick={handleDiscard}
                                disabled={busy}
                            >
                                Discard Changes
                            </button>
                        )}
                        <button
                            type="button"
                            className="writer-controls-delete"
                            onClick={handleDelete}
                            disabled={busy}
                        >
                            {deleting ? 'Deleting…' : (confirmDelete ? 'Confirm Delete' : 'Delete Series')}
                        </button>
                    </div>
                </form>
            )}
        </article>
    );
}

// "My Series" tab of the Author Portal: create series, chain your own
// documents into them in reading order, rename, reorder and delete.
// `onUnauthorized` runs when an admin request answers 401 (expired token).
function WriterSeries({
    posts = [],
    postsLoading = false,
    passphrase = '',
    adminToken = '',
    adminAuthorHash = '',
    seriesState,
    onUnauthorized,
}) {
    const isAdmin = isAdminToken(adminToken);
    const { series, setSeries, loading, error: loadError, reload } = seriesState;
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [openSeriesId, setOpenSeriesId] = useState('');

    const auth = useMemo(
        () => buildAuth({ passphrase, adminToken, adminAuthorHash }),
        [passphrase, adminToken, adminAuthorHash]
    );
    const postById = useMemo(
        () => new Map((Array.isArray(posts) ? posts : []).map((post) => [String(post._id), post])),
        [posts]
    );
    const sortedPosts = useMemo(
        () => [...(Array.isArray(posts) ? posts : [])].sort((a, b) => (
            new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
        )),
        [posts]
    );
    const seriesByPost = useMemo(() => indexSeriesByPost(series), [series]);

    const missingAuthor = isAdmin && !adminAuthorHash;

    const handleCreate = async (event) => {
        event.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Series name is required');
            return;
        }

        setCreating(true);
        setError('');
        setStatus('');
        try {
            const response = await axios.post(
                `${API_URL}/api/series`,
                { ...auth.body, name: trimmedName, description: description.trim(), postIds: [] },
                auth.config
            );
            const created = response.data?.series;
            if (created) {
                setSeries((previous) => [created, ...previous.filter((entry) => entry._id !== created._id)]);
                setOpenSeriesId(created._id);
            }
            setName('');
            setDescription('');
            setStatus('Series created — now add your documents to it below');
        } catch (requestError) {
            setError(describeError(requestError, 'Failed to create series'));
            if (isUnauthorized(requestError) && onUnauthorized) {
                onUnauthorized();
            }
        } finally {
            setCreating(false);
        }
    };

    const handleSaved = (updated) => {
        if (!updated || !updated._id) {
            reload();
            return;
        }
        setSeries((previous) => previous.map((entry) => (entry._id === updated._id ? updated : entry)));
    };

    // A card's own edits and saves make the list's last message (e.g.
    // "Series created …") stale; the card reports on itself.
    const clearStatus = () => setStatus('');

    const handleDeleted = (seriesId) => {
        setSeries((previous) => previous.filter((entry) => entry._id !== seriesId));
        setStatus('Series deleted — its documents stay published');
    };

    if (missingAuthor) {
        return <div className="no-posts">Select an author to manage their series.</div>;
    }
    // Neither a passphrase nor an admin token: the admin session has ended.
    if (!isAdmin && !passphrase) {
        return <div className="no-posts">Session ended — reopen the Author Portal to manage series.</div>;
    }

    return (
        <section className="writer-series">
            <header className="writer-series-header">
                <div>
                    <h3>My Series</h3>
                    <p>
                        Chain your documents into a numbered series. Series appear in the site&apos;s
                        Series section and sidebar, and each part links to the next.
                    </p>
                </div>
                <button
                    type="button"
                    className="author-refresh-button"
                    onClick={reload}
                    disabled={loading}
                >
                    Refresh
                </button>
            </header>

            {loadError && <div className="writer-controls-alert error">[ERROR] {loadError}</div>}
            {error && <div className="writer-controls-alert error">[ERROR] {error}</div>}
            {status && <div className="writer-controls-alert status">{status}</div>}

            <form className="writer-series-create" onSubmit={handleCreate}>
                <h4>New Series</h4>
                <label className="writer-controls-field">
                    <span>Series Name *</span>
                    <input
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        maxLength={NAME_MAX_LENGTH}
                        placeholder="e.g. World War Lies"
                        required
                    />
                </label>
                <label className="writer-controls-field">
                    <span>Description</span>
                    <textarea
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        rows={3}
                        placeholder="Optional — shown on the series page"
                    />
                </label>
                <div className="writer-controls-actions">
                    <button
                        type="submit"
                        className="writer-controls-submit"
                        disabled={creating || !name.trim()}
                    >
                        {creating ? 'Creating…' : 'Create Series'}
                    </button>
                </div>
            </form>

            {loading || postsLoading ? (
                <div className="loading">[Retrieving Series...]</div>
            ) : series.length === 0 ? (
                <div className="no-posts">No series yet. Create one above, then add your documents to it.</div>
            ) : (
                <div className="writer-series-list">
                    {series.map((entry) => (
                        <SeriesCard
                            key={entry._id}
                            entry={entry}
                            postById={postById}
                            sortedPosts={sortedPosts}
                            seriesByPost={seriesByPost}
                            auth={auth}
                            onSaved={handleSaved}
                            onDeleted={handleDeleted}
                            onActivity={clearStatus}
                            onUnauthorized={onUnauthorized}
                            defaultOpen={entry._id === openSeriesId}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

export default WriterSeries;
